import { memo, useState } from 'react';
import { COLORS, SHADOWS, LABELS } from '@/game/configs';
import { type Difficulty } from '@/game/configs';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { Trophy } from 'lucide-react';
interface LobbyScreenProps {
  onStartGame: () => void;
  onHowToPlay: () => void;
  difficulty: Difficulty;
  onDifficultyChange: (difficulty: Difficulty) => void;
}
interface BestTimes {
  easy: number | null;
  medium: number | null;
  hard: number | null;
}
export const LobbyScreen = memo(function LobbyScreen({
  onStartGame,
  onHowToPlay,
  difficulty,
  onDifficultyChange,
}: LobbyScreenProps) {
  const [bestTimes, setBestTimes] = useLocalStorage<BestTimes>('sudoku-best-times', {
    easy: null,
    medium: null,
    hard: null,
  });
  const [showDifficultyDropdown, setShowDifficultyDropdown] = useState(false);
  const formatTime = (seconds: number | null): string => {
    if (seconds === null) return LABELS.noTime;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const difficultyOptions: { value: Difficulty; label: string }[] = [
    { value: 'easy', label: LABELS.easy },
    { value: 'medium', label: LABELS.medium },
    { value: 'hard', label: LABELS.hard },
  ];
  const currentDifficultyLabel = difficultyOptions.find((d) => d.value === difficulty)?.label;
  return (
    <div
      className="flex flex-col items-center justify-center min-h-[100dvh] px-6 py-8"
      style={{
        backgroundColor: COLORS.cream,
        touchAction: 'manipulation',
      }}
    >
      <div className="flex flex-col items-center gap-8 w-full max-w-sm">
        <div className="flex flex-col items-center gap-2">
          <h1
            className="text-5xl sm:text-6xl font-extrabold tracking-tight"
            style={{
              fontFamily: 'Nunito, system-ui, sans-serif',
              color: COLORS.dark,
            }}
          >
            {LABELS.title}
          </h1>
          <p
            className="text-base"
            style={{
              fontFamily: 'Nunito, system-ui, sans-serif',
              color: COLORS.brown,
            }}
          >
            Classic Number Puzzle
          </p>
        </div>
        <div className="w-full">
          <div
            className="w-full rounded-2xl p-4"
            style={{
              backgroundColor: COLORS.ivory,
              boxShadow: SHADOWS.soft,
            }}
          >
            <p
              className="text-sm font-semibold mb-3"
              style={{
                fontFamily: 'Nunito, system-ui, sans-serif',
                color: COLORS.brown,
              }}
            >
              Best Times
            </p>
            <div className="flex flex-col gap-2">
              {difficultyOptions.map((opt) => (
                <div
                  key={opt.value}
                  className="flex justify-between items-center py-2 px-3 rounded-xl"
                  style={{
                    backgroundColor: COLORS.cream,
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{
                      fontFamily: 'Nunito, system-ui, sans-serif',
                      color: COLORS.dark,
                    }}
                  >
                    {opt.label}
                  </span>
                  <span
                    className="text-sm font-bold tabular-nums"
                    style={{
                      fontFamily: 'Nunito, system-ui, sans-serif',
                      color: bestTimes[opt.value] ? COLORS.dark : COLORS.brown,
                    }}
                  >
                    {formatTime(bestTimes[opt.value])}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="relative w-full dropdown-container">
          <button
            onClick={() => setShowDifficultyDropdown(!showDifficultyDropdown)}
            className="w-full py-4 rounded-2xl font-bold text-lg transition-all duration-150 flex items-center justify-between px-6"
            style={{
              backgroundColor: COLORS.ivory,
              boxShadow: SHADOWS.soft,
              fontFamily: 'Nunito, system-ui, sans-serif',
              color: COLORS.dark,
            }}
          >
            <span>{currentDifficultyLabel}</span>
            <svg
              className={`w-5 h-5 transition-transform ${showDifficultyDropdown ? 'rotate-180' : ''}`}
              fill="none"
              stroke={COLORS.brown}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showDifficultyDropdown && (
            <div
              className="absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden z-10"
              style={{
                backgroundColor: COLORS.cream,
                boxShadow: SHADOWS.deep,
              }}
            >
              {difficultyOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onDifficultyChange(opt.value);
                    setShowDifficultyDropdown(false);
                  }}
                  className="w-full py-3 px-6 text-left font-semibold transition-all duration-100"
                  style={{
                    fontFamily: 'Nunito, system-ui, sans-serif',
                    color: difficulty === opt.value ? COLORS.yellow : COLORS.dark,
                    backgroundColor:
                      difficulty === opt.value ? COLORS.ivory : 'transparent',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onStartGame}
          className="w-full py-5 rounded-2xl font-extrabold text-xl transition-all duration-150 active:scale-98"
          style={{
            backgroundColor: COLORS.yellow,
            boxShadow: '6px 6px 12px rgba(139, 115, 85, 0.3)',
            fontFamily: 'Nunito, system-ui, sans-serif',
            color: COLORS.dark,
          }}
        >
          {LABELS.startGame}
        </button>
        <button
          onClick={onHowToPlay}
          className="w-full py-4 rounded-2xl font-bold text-lg transition-all duration-150 active:scale-98"
          style={{
            backgroundColor: COLORS.ivory,
            boxShadow: SHADOWS.soft,
            fontFamily: 'Nunito, system-ui, sans-serif',
            color: COLORS.brown,
          }}
        >
          {LABELS.howToPlay}
        </button>
      </div>
    </div>
  );
});